import React, { useState, useCallback, useRef, useEffect } from 'react';
import { withRouter, Link } from 'react-router-dom';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import debounce from 'lodash/debounce';
import { FormattedMessage, injectIntl } from 'react-intl';

import ErrorMessage from 'UI/ErrorMessage/ErrorMessage';
import WithRegisterModal from 'components/Wrappers/WithRegisterModal/WithRegisterModal';

import { ReactComponent as Facebook } from 'assets/icons/facebook.svg';
import { ReactComponent as Twitter } from 'assets/icons/twitter.svg';
import { ReactComponent as YouTube } from 'assets/icons/youtube.svg';
import { ReactComponent as Instagram } from 'assets/icons/instagram.svg';
import { ReactComponent as Discord } from 'assets/icons/discord.svg';
// import { ReactComponent as Twitch } from 'assets/icons/twitch.svg';
import { ReactComponent as Right } from 'assets/icons/point_right_arrow.svg';
import { ReactComponent as Lock } from 'assets/icons/lock_alt.svg';

import { getUser } from 'store/user/selector';
import { getNewsletterState } from 'store/footer/selector';
import { getFooterRequest } from 'store/footer/actions';

import { checkValidity, updateObject } from 'shared/utility';

import { initialFormState } from './constants';

import {
    GAME_SETTINGS_URLS,
    MASTERCLASS_URLS,
    WATCH_HUB_URLS,
    RESULTS_HUB_URLS,
    MAIN_URLS,
    LEARN_HUB_URLS,
    GCT_URLS,
    PUZZLE,
} from 'shared/urlList';

import API from 'API';

import styles from './SwissKnifeMenu.module.scss';

const SwissKnifeMenu = ({ showMenu, closeMenu, history, location, showRegisterModal }) => {
	const [formControls, setFormControls] = useState(initialFormState);
	// eslint-disable-next-line
	const [formIsValid, setFormIsValid] = useState(false);
	// eslint-disable-next-line
    const [errorMessage, setErrorMessage] = useState('');
    const [joined, setJoined] = useState(
        useSelector(getNewsletterState, shallowEqual)
    );
    const [userInfo] = useState(useSelector(getUser, shallowEqual));
    const dispatch = useDispatch();
	const ref = useRef();

	useEffect(() => {
		showMenu && ref.current.dispatchEvent(new MouseEvent('mouseover'));
		ref.current.onmouseover = function() {
			ref.current.classList.add(styles.active);
		};
	}, [showMenu]);

	const navigate = (to = '/home') => {
		history.push(to);
	};

    const joinNewsletter = async () => {
        try {
            const payload = {
                email: formControls.email.value,
            };
            await API.post('/content/subscribe', payload);
            await dispatch(getFooterRequest());
            setJoined(true);
        } catch (error) {
			// eslint-disable-next-line
            console.warn('Newsletter', error);
			setErrorMessage(error.data.error);
        }
    };

    const formValidation = useCallback(
        debounce((formControls, value, identifier) => {
            if (value) {
                const val = checkValidity(
                    value,
                    formControls[identifier].validation
                );

                const isValid = val.isValid;
                const errorMessage = val.message;

                const updatedFormElement = updateObject(
                    formControls[identifier],
                    {
                        valid: isValid,
                        touched: true,
                        errorMessage,
                        value,
                    }
                );

                const controls = updateObject(formControls, {
                    [identifier]: updatedFormElement,
                });

                let formIsValid = true;

                // eslint-disable-next-line
                for (let identifier in controls) {
                    formIsValid = controls[identifier].valid && formIsValid;
                }

                setFormControls(controls);
                setFormIsValid(formIsValid);
            }
        }, 1000),
        []
    );

    const inputChangedHandler = (event, identifier) => {
        setErrorMessage('');
        setFormIsValid(false);

        const updatedFormElement = updateObject(formControls[identifier], {
            value: event.target.value.trim(),
            errorMessage: '',
            valid: true,
        });

        const controls = updateObject(formControls, {
            [identifier]: updatedFormElement,
        });

        setFormControls(controls);
        formValidation(formControls, event.target.value.trim(), identifier);
    };

	const removeActiveStyle = () => {
		ref.current.classList.remove(styles.active);
	};

	const { email } = formControls;

    return (
        <div
            className={[
                styles.swiss_knife_menu,
                showMenu ? styles.open : styles.close,
            ].join(' ')}
        >
            <div className={styles.wrapper}>
                <div className={styles.left_container}>
                    <div className={styles.content}>
                        <div className={styles.left_nav}>
                            <div
                                className={styles.section}
                                onClick={() => navigate(LEARN_HUB_URLS.LEARN)}
								ref={ref}
								onMouseOver={removeActiveStyle}
								onMouseOut={removeActiveStyle}
                            >
                                Learn
                                <div
                                    className={styles.section_inner}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div
                                        onClick={() =>
                                            navigate(LEARN_HUB_URLS.LESSONS)
                                        }
                                    >
                                        <div>Lessons</div>
                                    </div>
                                    <div
                                        onClick={
                                            userInfo ? () => navigate(PUZZLE.PUZZLE_HUB) : null
                                        }
                                    >
                                        <div
                                            className={
                                                userInfo
                                                    ? styles.unlocked
                                                    : styles.locked
                                            }
                                        >
                                            Train with puzzles{' '}
                                            {userInfo ? null : <Lock />}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div
                                className={styles.section}
                                onClick={() => navigate(MAIN_URLS.PLAYHUB)}
								onMouseOver={removeActiveStyle}
                            >
                                Play
                                <div
                                    className={styles.section_inner}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div
                                        onClick={() =>
                                            navigate(
                                                GAME_SETTINGS_URLS.SETTINGS_OPPONENT
                                            )
                                        }
                                    >
                                        <div>Quick play</div>
                                    </div>
                                    <div
                                        onClick={() =>
                                            navigate(
                                                GAME_SETTINGS_URLS.SETTINGS_COMPUTER
                                            )
                                        }
                                    >
                                        <div>Play vs computer</div>
                                    </div>
                                    <div
                                        onClick={
                                            userInfo
                                                ? () =>
                                                      navigate(
                                                          GAME_SETTINGS_URLS.SETTINGS_FRIEND
                                                      )
                                                : null
                                        }
                                    >
                                        <div
                                            className={
                                                userInfo
                                                    ? styles.unlocked
                                                    : styles.locked
                                            }
                                        >
                                            Challenge a friend{' '}
                                            {userInfo ? null : <Lock />}
                                        </div>
                                    </div>
                                    <div
                                        onClick={() =>
                                            navigate(
                                                RESULTS_HUB_URLS.GLOBAL_RANKING
                                            )
                                        }
                                    >
                                        <div>Global ranking</div>
                                    </div>
                                </div>
                            </div>
                            <div
                                className={styles.section}
                                onClick={() => navigate(WATCH_HUB_URLS.WATCH)}
								onMouseOver={removeActiveStyle}
                            >
                                Watch
                                <div
                                    className={styles.section_inner}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div
                                        onClick={() =>
                                            navigate(
                                                WATCH_HUB_URLS.DOCUMENTARIES
                                            )
                                        }
                                    >
                                        <div>Documentaries</div>
                                    </div>
                                    <div
                                        onClick={() =>
                                            navigate(WATCH_HUB_URLS.INTERVIEWS)
                                        }
                                    >
                                        <div>Interviews</div>
                                    </div>
                                    <div
                                        onClick={() =>
                                            navigate(
                                                WATCH_HUB_URLS.CHESS_JOURNEYS
                                            )
                                        }
                                    >
                                        <div>Chess journeys</div>
                                    </div>
                                    <div
                                        onClick={() =>
                                            navigate(WATCH_HUB_URLS.ALL_VIDEOS)
                                        }
                                    >
                                        <div>All videos</div>
                                    </div>
                                    <div
                                        onClick={
                                            userInfo
                                                ? () =>
                                                      navigate(
                                                          WATCH_HUB_URLS.LIVE_GAMES
                                                      )
                                                : null
                                        }
                                    >
                                        <div
                                            className={
                                                userInfo
                                                    ? styles.unlocked
                                                    : styles.locked
                                            }
                                        >
                                            Live games{' '}
                                            {userInfo ? null : <Lock />}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* <div className={styles.section} onClick={() => navigate(RESULTS_HUB_URLS.RESULTS_HUB)}>
								Results.
								<div className={styles.section_inner} onClick={(e) => e.stopPropagation()}>
									<div>GCT results.</div>
									<div>Fide Results.</div>
									<div>Calendar.</div>
								</div>
							</div> */}
                        </div>
                    </div>
                    <div className={styles.footer}>
                        {/*
                        <div className={styles.social}>
                            <a
                                href="https://www.facebook.com/Kasparovchess-104990161721049"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.facebook}
                            >
                                <Facebook />
                            </a>
                            <a
                                href="https://twitter.com/Kasparovchess"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.twitter}
                            >
                                <Twitter />
                            </a>
                            <a
                                href="https://www.youtube.com/channel/UCfGw2EKElc4eRh9r4iwy8yQ"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.youtube}
                            >
                                <YouTube />
                            </a>
                            <a
                                href="https://www.instagram.com/kasparovchess_"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.instagram}
                            >
                                <Instagram />
                            </a>
														<a
								href="https://discord.gg/MBD4RamdcX"
								target="_blank"
								rel="noopener noreferrer"
								className={styles.discord}
							>
								<Discord />
							</a>
                            {/* <a
								href="https://www.twitch.tv"
								target="_blank"
								rel="noopener noreferrer"
								className={styles.instagram}
							>
								<Twitch />
							</a> */}
                        {/*</div>
                        */}
                        <div className={styles.right_content}>
                            <Link to='/legacy'>Legacy</Link>
                            <Link to='/support_articles/how-do-the-rankings-work-1'>Scoring system</Link>
                            <Link to='/chess-rules'>Chess rules</Link>
                            {/* {userInfo && <span>Shop</span>} */}
                        </div>
                    </div>
                </div>
                <div className={styles.right_container}>
                    {userInfo && (
                        <section className={styles.section1}>
                            <h2>Need help?</h2>
                            <div
                                onClick={() => navigate('/faq-support/request')}
                            >
                                Contact us <Right />
                            </div>
                        </section>
                    )}
                    {userInfo && <div className="separator"></div>}
                    <section
                        className={styles.section2}
                        style={{ paddingTop: userInfo ? '40px' : '0px' }}
                    >
                        <h2>Newsletter</h2>
                        <span>
                            Want to stay updated with the latest content and
                            events from K-chess?
                        </span>
                        <div className={styles.email}>
                            {!joined && (
                                <input
                                    {...email.elementConfig}
                                    value={email.value}
                                    onChange={(e) =>
                                        inputChangedHandler(e, 'email')
                                    }
                                    noValidate
                                    className={
                                        !email.errorMessage && !errorMessage
                                            ? null
                                            : styles.invalid
                                    }
                                />
                            )}
                            <div
                                role="button"
                                className={[
                                    styles.button,
                                    joined ? styles.joined : null,
                                ].join(' ')}
                                onClick={
                                    !email.errorMessage && !errorMessage && email.value
                                        ? joinNewsletter
                                        : null
                                }
                            >
                                {joined ? (
                                    <svg
                                        className={styles.animated_check}
                                        viewBox="0 0 24 24"
                                    >
                                        <path d="M4.1 12.7L9 17.6 20.3 6.3" />
                                    </svg>
                                ) : (
                                    'Subscribe'
                                )}
                            </div>
                        </div>
                        <ErrorMessage show={email.errorMessage}>
                            <FormattedMessage
                                id={email.errorMessage}
                                values={{
                                    maxLength: email.validation.maxLength,
                                    minLength: email.validation.minLength,
                                }}
                            />
                        </ErrorMessage>
						<ErrorMessage show={errorMessage}>
							<FormattedMessage
								id={errorMessage && `error.${errorMessage}`}
							/>
						</ErrorMessage>
                        {joined && (
                            <div className={styles.thanks}>
                                Thank you, we’ll keep you posted!
                            </div>
                        )}
                    </section>
                    <div className="separator"></div>
                    {userInfo ? (
                        userInfo.user_tier === 'free' && (!userInfo.subscription || (userInfo.subscription.status && (userInfo.subscription.status === 'expired' || userInfo.subscription.status === 'canceled'))) ? (
                            <section className={styles.section3}>
                                <h2>Become a pro member</h2>
                                <button
                                    className={styles.upgrade}
                                    onClick={location.pathname.includes('upgrade-account') ? closeMenu : () => navigate('/upgrade-account')}
                                >
                                    Upgrade
                                </button>
                            </section>
                        ) : null
                    ) : (
                        <section className={styles.section3}>
                            <h2>Sign up for free</h2>
                            <button
                                className={styles.upgrade}
                                onClick={() => navigate('/onboarding')}
                            >
                                Sign up
                            </button>
                        </section>
                    )}
                </div>
            </div>
        </div>
    );
};

export default withRouter(WithRegisterModal(injectIntl(SwissKnifeMenu)));
